import { useTranslation } from "react-i18next";
import { usePaginationState, updatePaginationState } from "@/store/paginationStore";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Filter } from "lucide-react";
import { useState, useEffect } from "react";
import { convertDecimalToBp, convertBpToDecimal } from "@/utils/helpers";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { FormattedNumberInputField } from "@/components/shared/form";

/**
 * Configuration for a single value range field
 */
interface ValueRangeFieldConfig {
  /** The field name in the filters object */
  fieldName: string;
  /** Translation key for the field label */
  labelKey: string;
  /** Input style: "decimal" for percentages, "currency" for money, "integer" for whole numbers */
  inputStyle: "decimal" | "currency" | "integer";
  /** Whether to convert between decimal and basis points (for percentages) */
  useBasisPoints: boolean;
  /** Suffix to display (e.g., '%', '€') */
  suffix?: string;
  /** Decimal scale for the input */
  decimalScale?: number;
  /** Placeholder for the input */
  placeholder?: string;
}

/**
 * Configuration interface for the generic ValueRangeFilter component
 */
interface ValueRangeConfig<QueryVariables> {
  /** The pagination state key (e.g., "paginatedTariffs", "paginatedHolidays") */
  paginationStateKey: keyof PaginationState;
  /** Array of field configurations */
  fields: ValueRangeFieldConfig[];
  /** Translation key for the filter button text when no filters are active */
  defaultButtonTextKey: string;
  /** Function to build query variables with current filters and value ranges */
  buildQueryVariables: (
    _displayAmount: number,
    _filters: any,
    _valueRanges: Record<string, { gte: number | null; lte: number | null }>,
    _sort?: any
  ) => QueryVariables;
  /** Function to extract filters from pagination state */
  extractFilters: (_paginationState: any) => any;
}

interface Props<QueryVariables> {
  displayAmount: number;
  handleSubmitClick: (_variables: QueryVariables) => void;
  config: ValueRangeConfig<QueryVariables>;
}

/**
 * Generic ValueRangeFilter component
 * A reusable value range picker for filtering data by numeric ranges.
 * Can be configured for different configuration types with multiple fields.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - The number of items to display per page
 * @param {Function} props.handleSubmitClick - Callback invoked with updated query variables when value ranges change
 * @param {ValueRangeConfig} props.config - Configuration object defining the behavior for this specific use case
 * @returns {JSX.Element} A value range picker component
 *
 * @example
 * // For vacation bonus with 2 fields
 * <ValueRangeFilter
 *   displayAmount={20}
 *   handleSubmitClick={handleVacationBonusValueChange}
 *   config={vacationBonusValueConfig}
 * />
 */
const ValueRangeFilter = <QueryVariables,>(props: Props<QueryVariables>) => {
  // Props
  const { displayAmount, handleSubmitClick, config } = props;

  // Hooks
  const { t } = useTranslation(["translation", "common", "components"]);

  // Pagination
  const paginationState = usePaginationState(config.paginationStateKey);
  const filters = config.extractFilters(paginationState);

  // State to track if popover is open
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Helper function to get current field values
  const getCurrentFieldValues = () => {
    const values: Record<string, { gte?: number; lte?: number }> = {};

    config.fields.forEach(field => {
      const fieldData = filters[field.fieldName];
      if (fieldData) {
        if (field.useBasisPoints) {
          values[field.fieldName] = {
            gte: fieldData.gte ? convertBpToDecimal(fieldData.gte || 0) : undefined,
            lte: fieldData.lte ? convertBpToDecimal(fieldData.lte || 0) : undefined,
          };
        } else {
          values[field.fieldName] = {
            gte: fieldData.gte || undefined,
            lte: fieldData.lte || undefined,
          };
        }
      }
    });

    return values;
  };

  const currentValues = getCurrentFieldValues();

  // React Hook Form setup
  const form = useForm({
    defaultValues: Object.fromEntries(
      config.fields.flatMap(field => [
        [`${field.fieldName}Gte`, currentValues[field.fieldName]?.gte],
        [`${field.fieldName}Lte`, currentValues[field.fieldName]?.lte],
      ])
    ),
    mode: "onChange",
  });

  // Reset form when popover opens to ensure it shows current state
  useEffect(() => {
    if (isPopoverOpen) {
      const newValues = getCurrentFieldValues();
      const formValues = Object.fromEntries(
        config.fields.flatMap(field => [
          [`${field.fieldName}Gte`, newValues[field.fieldName]?.gte],
          [`${field.fieldName}Lte`, newValues[field.fieldName]?.lte],
        ])
      );
      form.reset(formValues);
    }
  }, [isPopoverOpen, form]);

  // Handle Enter key press to submit the form
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isPopoverOpen && event.key === "Enter") {
        event.preventDefault();
        submitFilters();
      }
    };

    if (isPopoverOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPopoverOpen]);

  /**
   * Handles changing the value ranges
   */
  const submitFilters = () => {
    const formValues = form.getValues();

    // Helper function to check if a value is valid
    const isValidValue = (value: any): boolean => {
      return value !== undefined && value !== null && value !== "" && !isNaN(value);
    };

    // Process each field
    const valueRanges: Record<string, { gte: number | null; lte: number | null }> = {};

    config.fields.forEach(field => {
      const gteValue = formValues[`${field.fieldName}Gte`];
      const lteValue = formValues[`${field.fieldName}Lte`];

      const fieldRange: any = {};

      if (isValidValue(gteValue)) {
        fieldRange.gte = field.useBasisPoints
          ? convertDecimalToBp(Number(gteValue))
          : Number(gteValue);
      }

      if (isValidValue(lteValue)) {
        fieldRange.lte = field.useBasisPoints
          ? convertDecimalToBp(Number(lteValue))
          : Number(lteValue);
      }

      // Only include the field if it has valid values
      if (Object.keys(fieldRange).length > 0) {
        valueRanges[field.fieldName] = fieldRange;
      } else {
        valueRanges[field.fieldName] = { gte: null, lte: null };
      }
    });

    // Update pagination state with new value ranges
    const updatedFilters = { ...filters };
    config.fields.forEach(field => {
      const fieldRange = valueRanges[field.fieldName];
      // Only include the field if it has at least one valid value (not both null)
      if (fieldRange.gte !== null || fieldRange.lte !== null) {
        updatedFilters[field.fieldName] = fieldRange;
      } else {
        // Remove the field from filters if both values are null
        delete updatedFilters[field.fieldName];
      }
    });

    updatePaginationState({
      [config.paginationStateKey]: {
        filters: updatedFilters
      }
    });

    // Filter out fields with both null values from valueRanges before passing to query
    const filteredValueRanges: Record<string, { gte: number | null; lte: number | null }> = {};
    Object.entries(valueRanges).forEach(([fieldName, range]) => {
      if (range.gte !== null || range.lte !== null) {
        filteredValueRanges[fieldName] = range;
      }
    });

    // Build and submit query variables with current sort preserved
    const currentSort = (paginationState as any)?.sort;
    const baseVariables = config.buildQueryVariables(displayAmount, updatedFilters, filteredValueRanges, currentSort);

    const updatedVariables = { ...baseVariables };

    handleSubmitClick(updatedVariables);
    setIsPopoverOpen(false);
  };

  // Determine if any filter is active
  const activeFields = config.fields.filter(field => {
    const fieldData = filters[field.fieldName];
    return fieldData && (fieldData.gte || fieldData.lte);
  });

  const isAnyFilterActive = activeFields.length > 0;

  // Create display text for the trigger button
  const getDisplayText = () => {
    if (!isAnyFilterActive) {
      return t(config.defaultButtonTextKey);
    }

    // If only one field, show the actual values
    if (config.fields.length === 1) {
      const field = config.fields[0];
      const fieldData = filters[field.fieldName];

      if (fieldData) {
        const gte = field.useBasisPoints ? convertBpToDecimal(fieldData.gte || 0) : fieldData.gte;
        const lte = field.useBasisPoints ? convertBpToDecimal(fieldData.lte || 0) : fieldData.lte;

        if (gte && lte) {
          return `${gte}${field.suffix || ""} - ${lte}${field.suffix || ""}`;
        } else if (gte) {
          return `≥ ${gte}${field.suffix || ""}`;
        } else if (lte) {
          return `≤ ${lte}${field.suffix || ""}`;
        }
      }
    }

    // If multiple fields, show count
    return `${activeFields.length} ${t("common:filters")}`;
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-40 justify-start text-left font-normal"
        >
          <div className="flex items-center gap-1">
            <Filter className="h-4 w-4" />
          </div>
          <span className={cn(!isAnyFilterActive && "text-muted-foreground")}>
            {getDisplayText()}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-4" align="start">
        <Form {...form}>
          <div className="space-y-4">
            {config.fields.map((field) => (
              <div key={field.fieldName} className="space-y-1">
                <Label className="text-sm font-semibold">
                  {t(field.labelKey)}
                </Label>
                <div className="flex flex-row gap-4 items-center">
                  <div className="space-y-2">
                    <FormattedNumberInputField
                      control={form.control}
                      name={`${field.fieldName}Gte`}
                      label=""
                      isStandalone={true}
                      style={field.inputStyle === "integer" ? "decimal" : field.inputStyle}
                      additionalSuffix={field.suffix}
                      decimalScale={field.decimalScale}
                      placeholder={field.placeholder || "0"}
                      className="w-full"
                    />
                  </div>
                  <div className="text-sm text-gray-500 pt-2">{t("common:to")}</div>
                  <div className="space-y-2">
                    <FormattedNumberInputField
                      control={form.control}
                      name={`${field.fieldName}Lte`}
                      label=""
                      isStandalone={true}
                      style={field.inputStyle === "integer" ? "decimal" : field.inputStyle}
                      additionalSuffix={field.suffix}
                      decimalScale={field.decimalScale}
                      placeholder={field.placeholder || "100"}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button onClick={submitFilters} size="sm" className="flex items-center gap-2">
                {t("common:buttons.submit")}
              </Button>
            </div>
          </div>
        </Form>
      </PopoverContent>
    </Popover>
  );
};

export default ValueRangeFilter;
